import { type CSSProperties } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { CalendarCheck, PackageOpen } from "lucide-react"
import Header from "../../../components/header"
import { Project } from "../../../types/project.type"
import {
  getReadablePrimaryButtonStyle,
  resolveThemeColor,
} from "../../../utils/buttonColor"

function HeroSection({
  project,
  openLoginModal,
  canRent = false,
}: {
  project: Project
  openLoginModal: () => void
  /** Hidden for projects that rent nothing out, so the link never leads to an empty page. */
  canRent?: boolean
}) {
  const navigate = useNavigate()
  const { t } = useTranslation('home')
  const themeColor = resolveThemeColor(project.themeColor)
  const primaryButtonStyle = getReadablePrimaryButtonStyle(project.buttonColor)
  const primaryButtonClass =
    "inline-flex w-full items-center justify-center gap-2 rounded-xl px-8 py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95 sm:w-auto"
  const coverUrl = project.coverImageFiles?.[0]?.s3Url

  const backgroundStyles: CSSProperties = coverUrl
    ? {
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.2) 0%, rgba(15, 23, 42, 0.38) 100%), linear-gradient(135deg, ${themeColor}18 0%, transparent 50%), url(${JSON.stringify(coverUrl)})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {
        background: `linear-gradient(160deg, ${themeColor} 0%, ${themeColor}dd 45%, ${themeColor}aa 100%)`,
      }

  return (
    <section
      className="relative flex min-h-[min(100dvh,920px)] flex-col md:min-h-screen"
      style={backgroundStyles}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent md:from-black/15" aria-hidden />

      <Header openLoginModal={openLoginModal} project={project} />

      <div className="relative z-[1] flex flex-1 flex-col items-center justify-center px-5 pb-12 pt-6 md:pb-16 md:pt-10">
        <div
          className="w-full max-w-xl rounded-2xl border border-gray-200/80 bg-white/95 px-6 py-8 text-center shadow-xl shadow-gray-900/10 backdrop-blur-sm sm:max-w-2xl sm:px-10 sm:py-10"
          style={{
            boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px ${themeColor}14`,
          }}
        >
          {project.motto ? (
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em] sm:text-sm"
              style={{ color: themeColor }}
            >
              {project.motto}
            </p>
          ) : null}
          <h1
            className={`text-balance text-3xl font-semibold leading-tight tracking-tight text-gray-900 sm:text-4xl md:text-[2.5rem] md:leading-[1.15] ${project.motto ? "mt-3 sm:mt-4" : ""}`}
          >
            {project.welcomeMessage}
          </h1>
          {/* Both actions in the same solid style and the same colour: they are two ways in,
              not an action and its refusal, which is how a filled button beside an outlined
              one reads. */}
          <div className="mt-8 flex flex-col items-stretch gap-3 sm:mt-10 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => navigate("/booking")}
              className={primaryButtonClass}
              style={primaryButtonStyle}
            >
              <CalendarCheck className="h-4 w-4 shrink-0" aria-hidden />
              {t('bookNow')}
            </button>
            {canRent ? (
              <button
                type="button"
                onClick={() => navigate("/rent")}
                className={primaryButtonClass}
                style={primaryButtonStyle}
              >
                <PackageOpen className="h-4 w-4 shrink-0" aria-hidden />
                {t('rentAnItem')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection
